import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { Loader2, ShieldAlert, AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  redirectTo?: string;
}

// Maximum time to wait for auth check before showing timeout
const AUTH_TIMEOUT_MS = 10000; // 10 seconds

export const ProtectedRoute = ({
  children,
  requireAdmin = false,
  redirectTo = '/auth'
}: ProtectedRouteProps) => {
  const { user, loading, isAdmin, authError } = useAuth();
  const location = useLocation();
  const [timedOut, setTimedOut] = useState(false);

  // Set timeout for auth loading
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        setTimedOut(true);
      }, AUTH_TIMEOUT_MS);

      return () => clearTimeout(timer);
    } else {
      setTimedOut(false);
    }
  }, [loading]);

  // Show loading state with timeout handling
  if (loading) {
    if (timedOut) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="text-center p-8 max-w-md">
            <AlertCircle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
            <h1 className="text-xl font-semibold mb-2">Authentication Timeout</h1>
            <p className="text-gray-600 mb-6">
              We're having trouble verifying your session. Please try again.
            </p>
            <Button
              onClick={() => window.location.reload()}
              className="mr-2"
            >
              Retry
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = redirectTo}
            >
              Go to Login
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Handle auth errors
  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center p-8 max-w-md">
          <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
          <h1 className="text-xl font-semibold mb-2">Authentication Error</h1>
          <p className="text-gray-600 mb-6">{authError}</p>
          <Button onClick={() => window.location.href = redirectTo}>
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    // Save the attempted URL for redirect after login
    return (
      <Navigate
        to={redirectTo}
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center p-8 max-w-md bg-white rounded-xl shadow-lg">
          <ShieldAlert className="h-16 w-16 mx-auto text-red-500 mb-4" />
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-6">
            You don't have permission to access this page.
            Admin privileges are required.
          </p>
          <div className="space-x-2">
            <Button
              variant="outline"
              onClick={() => window.history.back()}
            >
              Go Back
            </Button>
            <Button
              onClick={() => window.location.href = '/'}
            >
              Go to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};